import { useMemo } from 'react';

import { Card, Form, Typography } from '@arco-design/web-react';

import { ServicePreviewSection } from '../../../capabilities/service/ServicePreviewSection';
import { StoragePreviewSection } from '../../../capabilities/storage/StoragePreviewSection';
import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import type { VmSpec } from '../headless/schema';
import { VM_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'name',
  'description',
  'namespace',
  'workspace',
  'datacenter',
  'runStrategy',
  'system',
  'systemDisk',
  'resource',
  'dataDisks',
  'volumes',
  'optionMeta',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '未配置';
const ALL_MODULE_KEYS = VM_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]): Map<string, DisplayDescriptor[]> {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

function partitionModuleItems(items: DisplayDescriptor[]) {
  const subsections: DisplayDescriptor[] = [];
  const flat: DisplayDescriptor[] = [];

  for (const item of items) {
    if (item.children?.length) {
      subsections.push(item);
    } else {
      flat.push(item);
    }
  }

  return { flat, subsections };
}

function renderModuleContent(
  modKey: string,
  items: DisplayDescriptor[],
  services: VmSpec['resource']['network']['service'],
  dataDisks: VmSpec['dataDisks'],
  optionMetaForm: Record<string, unknown>,
) {
  const { flat, subsections } = partitionModuleItems(items);
  const column = modKey === 'baseInfo' ? 2 : 1;

  if (modKey === 'service') {
    return <ServicePreviewSection services={services} />;
  }

  if (modKey === 'dataDisk') {
    return <StoragePreviewSection disks={dataDisks} optionMetaForm={optionMetaForm} />;
  }

  if (modKey === 'systemSetting') {
    const displayItems = flat.length ? flat : [{ id: `${modKey}-empty`, group: modKey, label: '-', value: PREVIEW_EMPTY }];
    return <DescriptorDescriptions items={displayItems} column={1} />;
  }

  if (subsections.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {subsections.map((sub) => (
          <div key={sub.id}>
            <Typography.Text bold style={{ display: 'block', marginBottom: 8 }}>
              {sub.label}
            </Typography.Text>
            <DescriptorDescriptions items={sub.children!} column={1} />
          </div>
        ))}
      </div>
    );
  }

  const displayItems = flat.length ? flat : [{ id: `${modKey}-empty`, group: modKey, label: '-', value: PREVIEW_EMPTY }];
  return <DescriptorDescriptions items={displayItems} column={column} />;
}

/** VirtualMachine ResourcePreview：form / spec → 按模块分组的确认页摘要（样式对齐应用实例） */
export function VmResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('VirtualMachine');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const formValues = form?.getFieldsValue() ?? {};
  const skuResourceModules = formValues.skuResourceModules as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'VirtualMachine', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    namespace: ctx?.namespace ?? (formValues.namespace as string | undefined),
    cluster: ctx?.cluster ?? (formValues.datacenter as string | undefined),
    sku: { enabledModules: previewEnabledModules },
  };

  const resolvedSpec = (spec ?? plugin.pipeline.schema.parse(plugin.pipeline.fromForm(formValues, previewCtx))) as VmSpec;

  const profile = plugin.pipeline.projectionProfile;
  const flatItems =
    descriptors ?? (isSkuCapableProfile(profile) ? profile.projections.buildDisplayDescriptors(resolvedSpec, previewCtx) : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const services = resolvedSpec.resource?.network?.service;
  const optionMetaForm = formValues as Record<string, unknown>;
  const visibleModules = VM_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => {
        const items = grouped.get(mod.key) ?? [{ id: `${mod.key}-empty`, group: mod.key, label: '-', value: PREVIEW_EMPTY }];

        return (
          <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
            {renderModuleContent(mod.key, items, services, resolvedSpec.dataDisks, optionMetaForm)}
          </Card>
        );
      })}
    </>
  );
}
